import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { X, Download } from "lucide-react";

export default function InstallPromptBubble() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const location = useLocation();

  // Check if we're on landing page and on mobile/tablet
  const isLandingPage = location.pathname === "/" || location.pathname === "";

  useEffect(() => {
    // Check screen size for mobile/tablet (less than 1024px)
    const checkDevice = () => {
      setIsMobileOrTablet(window.innerWidth < 1024);
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  useEffect(() => {
    // Only show on landing page
    if (!isLandingPage) {
      setShow(false);
      return;
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    // Check if user dismissed it before
    const wasDismissed = localStorage.getItem('tireo_install_dismissed');
    if (wasDismissed) {
      const dismissedAt = new Date(wasDismissed);
      const hoursSince = (Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60);
      if (hoursSince < 24) {
        return;
      }
    }

    // Listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShow(true), 1500);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Show after delay
    const timer = setTimeout(() => setShow(true), 2000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(timer);
    };
  }, [isLandingPage]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShow(false);
      }
      setDeferredPrompt(null);
    } else {
      const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);
      
      if (isIOS) {
        alert(
          "Pour installer TIREO :\n\n" +
          "1. Appuyez sur ⬆️ (partager)\n" +
          "2. 'Sur l'écran d'accueil'\n" +
          "3. 'Ajouter'"
        );
      } else if (isAndroid) {
        alert(
          "Pour installer TIREO :\n\n" +
          "Appuyez sur le menu ⋮ puis\n'Installer l'application'"
        );
      }
    }
    handleDismiss();
  };

  const handleDismiss = () => {
    setShow(false);
    setDismissed(true);
    localStorage.setItem('tireo_install_dismissed', new Date().toISOString());
  };

  // Don't show if not on landing page, dismissed, or hidden
  if (!show || dismissed || !isLandingPage) return null;

  return (
    <div 
      className="w-full z-50 animate-in slide-in-from-top duration-300"
      data-testid="install-prompt-bubble"
    >
      <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-4 py-2 flex items-center justify-between gap-3 shadow-md">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-6 h-6 rounded-md bg-white/20 flex items-center justify-center flex-shrink-0 animate-pulse">
            <span className="text-xs font-bold text-white">T</span>
          </div>
          <p className="text-white text-sm font-medium truncate">
            Installer TIREO sur votre appareil
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleInstall}
            className="bg-white text-blue-600 px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 hover:bg-blue-50 transition-all hover:scale-105 animate-bounce-subtle"
            data-testid="install-btn"
          >
            <Download className="w-3.5 h-3.5" />
            Installer
          </button>
          <button 
            onClick={handleDismiss}
            className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
            aria-label="Fermer"
            data-testid="install-dismiss-btn"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
}
